<template>
    <p class="clamped" :class="{ 'clamped-open': expanded }">
        <!-- Clamped by lines, not characters, so what fits depends on the width
             the text actually has: zoom out and more of it shows, and "Show more"
             still sits at the end of the last visible line. Kept inside the <p>:
             a comment beside it would make the root a fragment, and the classes
             a parent passes would not land. -->
        <span ref="text" class="clamped-text" :style="{ '--clamped-lines': lines }">{{ text }}</span>
        <button v-if="overflowing && !expanded" type="button" class="clamped-toggle clamped-more"
            aria-expanded="false" @click="expanded = true">
            Show more
        </button>
        <button v-if="expanded" type="button" class="clamped-toggle" aria-expanded="true" @click="expanded = false">
            Show less
        </button>
    </p>
</template>

<script>
export default {
    name: 'ClampedText',
    props: {
        text: { type: String, default: '' },
        /** Lines shown before "Show more". */
        lines: { type: Number, default: 3 },
    },
    data() {
        return {
            expanded: false,
            // Whether the clamp is hiding anything. Measured, not guessed: the
            // same text overflows at one width and fits at another.
            overflowing: false,
        };
    },
    watch: {
        text() {
            this.expanded = false;
            this.$nextTick(this.measure);
        },
        expanded(open) {
            // Collapsing re-applies the clamp, so measure what it hides now.
            if (!open) this.$nextTick(this.measure);
        },
    },
    mounted() {
        this.measure();
        // Zoom and window resizes both change the element's width, which is
        // what decides how many lines the text needs.
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => this.measure());
            this._resizeObserver.observe(this.$refs.text);
        }
    },
    beforeUnmount() {
        this._resizeObserver?.disconnect();
    },
    methods: {
        measure() {
            const el = this.$refs.text;
            // While open there is no clamp to measure against; keep "Show less".
            if (!el || this.expanded) return;
            this.overflowing = el.scrollHeight > el.clientHeight + 1;
        },
    },
};
</script>

<style scoped>
.clamped {
    position: relative;
    overflow-wrap: anywhere;
}

.clamped-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--clamped-lines);
    overflow: hidden;
}

.clamped-open .clamped-text {
    display: inline;
    -webkit-line-clamp: unset;
}

.clamped-toggle {
    padding: 0;
    border: 0;
    background: none;
    font-size: inherit;
    line-height: inherit;
    font-weight: 600;
    color: #2c7be5;
    white-space: nowrap;
}

.clamped-open .clamped-toggle {
    margin-left: 0.25rem;
}

.clamped-toggle:hover {
    text-decoration: underline;
}

/* Laid over the end of the last visible line, fading the words it covers. */
.clamped-more {
    position: absolute;
    right: 0;
    bottom: 0;
    padding-left: 2.5em;
    background: linear-gradient(to right, transparent, var(--clamped-bg, #fff) 2em);
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` is set outside this component. */
body.dark-mode .clamped-more {
    --clamped-bg: #{$dark-card-bg};
}
</style>
